import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/useToast';
import { Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImportCourierInvoiceFile } from '../../hooks/useImportCourierInvoiceFile';

const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = '.csv,.txt';

interface ImportCourierInvoiceModalProps {
  invoiceId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const ImportCourierInvoiceModal = ({ invoiceId, isOpen, onClose }: ImportCourierInvoiceModalProps) => {
  const { t } = useTranslation('courierInvoicesModule');
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const { importFileAsync, isImporting } = useImportCourierInvoiceFile(invoiceId);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPeriodStart('');
      setPeriodEnd('');
      if (fileRef.current) {
        fileRef.current.value = '';
      }
    }
  }, [isOpen]);

  const onPickFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    const name = f.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.txt')) {
      toast({ title: t('import.error_title'), description: t('import.invalid_type'), variant: 'destructive' });
      return;
    }
    if (f.size > MAX_BYTES) {
      toast({ title: t('import.error_title'), description: t('import.too_large'), variant: 'destructive' });
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) {
      return;
    }
    try {
      await importFileAsync({
        file,
        ...(periodStart ? { period_start: periodStart } : {}),
        ...(periodEnd ? { period_end: periodEnd } : {}),
      });
      onClose();
    } catch {
      /* toast en hook */
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg bg-background border-border">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t('import.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>{t('import.file_label')}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={isImporting}
              >
                <Upload className="mr-2 h-4 w-4" />
                {file?.name ?? t('import.file_label')}
              </Button>
              {file ? (
                <span className="text-sm text-muted-foreground truncate max-w-[240px]" title={file.name}>
                  {file.name}
                </span>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>{t('import.period_start')}</Label>
              <Input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                disabled={isImporting}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('import.period_end')}</Label>
              <Input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                disabled={isImporting}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isImporting}>
            {t('import.cancel')}
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={isImporting || !file}>
            {t('import.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
